// FILE: frontend/src/components/kyb/FooterMetadata.tsx
// PURPOSE: Add PDF export button with download functionality
// ACTION: Replace entire file with this code

import React, { useState } from 'react';
import { Database, Calendar, Clock, ChevronRight, Download } from 'lucide-react';
import type { ApiResult } from '../../types/kyb.types';

interface FooterMetadataProps {
  analysisMethod?: string;
  version?: string;
  timestamp?: string;
  results?: ApiResult;
  getToken?: () => Promise<string | null>;
}

export const FooterMetadata: React.FC<FooterMetadataProps> = ({ 
  analysisMethod = 'AI-Powered Analysis',
  version = 'enhanced_v3.1',
  timestamp,
  results,
  getToken
}) => {
  const [isExporting, setIsExporting] = useState(false);

  const formatTimestamp = (ts?: string): string => {
    if (!ts) return new Date().toLocaleString();
    try {
      const date = new Date(ts);
      return date.toLocaleString('en-US', {
        month: 'numeric', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true
      });
    } catch {
      return ts;
    }
  };

  const handleExportPDF = async () => {
    if (!results) {
      alert('No results available to export');
      return;
    }

    setIsExporting(true);
    
    try {
      // Get authentication token
      let token: string | null = null;
      if (getToken) {
        token = await getToken();
        if (!token) {
          alert('⚠️ Authentication Required\n\nYou need to be signed in to export reports. Please sign in and try again.');
          setIsExporting(false);
          return;
        }
      }

      // Build headers with auth token
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch('/api/export-pdf', {
        method: 'POST',
        headers,
        body: JSON.stringify({ analysis_data: results })
      });

      // Handle authentication errors specifically
      if (response.status === 401 || response.status === 403) {
        alert('🔒 Authentication Failed\n\nYour session has expired or is invalid. Please sign in again to export the report.');
        setIsExporting(false);
        return;
      }

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Export failed: ${response.status} ${errorText}`);
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'KYB_Report.pdf';
      
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match?.[1]) filename = match[1].replace(/['"]/g, '');
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('PDF export failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(`❌ PDF Export Failed\n\n${errorMessage}\n\nPlease try again or contact support if the issue persists.`);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="border-t-2 border-slate-200 pt-6 mt-8">
      <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl p-6 border-2 border-slate-200">
        <div className="flex items-center justify-between flex-wrap gap-4">
          
          {/* Left - Metadata */}
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Database className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium">Analysis Method</p>
                <p className="text-sm font-bold text-slate-900">{analysisMethod}</p>
              </div>
            </div>

            <div className="w-px h-10 bg-slate-300" />

            <div className="flex items-center gap-2">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Calendar className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium">Version</p>
                <p className="text-sm font-bold text-slate-900">{version}</p>
              </div>
            </div>

            <div className="w-px h-10 bg-slate-300" />

            <div className="flex items-center gap-2">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Clock className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium">Timestamp</p>
                <p className="text-sm font-bold text-slate-900">{formatTimestamp(timestamp)}</p>
              </div>
            </div>
          </div>

          {/* Right - Export Button */}
          <button 
            onClick={handleExportPDF}
            disabled={isExporting || !results}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 
                       hover:from-blue-700 hover:to-purple-700 text-white font-bold rounded-xl shadow-lg 
                       hover:shadow-xl transition-all duration-300 transform hover:scale-105 
                       disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {isExporting ? (
              <>
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
                Generating...
              </>
            ) : (
              <>
                <Download className="w-5 h-5" />
                Export Report
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>

        {/* Disclaimer */}
        <div className="mt-4 pt-4 border-t border-slate-200">
          <p className="text-xs text-slate-600">
            <span className="font-bold">Disclaimer:</span> This analysis uses AI and public data. 
            Always conduct thorough due diligence and consult advisors before business decisions.
          </p>
        </div>
      </div>
    </div>
  );
};

export default FooterMetadata;